import React from 'react';
import { useTheme } from '../../context/ThemeContext';
import { motion } from 'framer-motion';
import { SunIcon, MoonIcon } from '@heroicons/react/solid';

interface ThemeToggleProps {
  className?: string;
}

const ThemeToggle: React.FC<ThemeToggleProps> = ({ className = '' }) => {
  const { isDarkMode, toggleTheme } = useTheme();

  return (
    <motion.button
      onClick={toggleTheme}
      className={`relative h-10 w-10 rounded-full p-1 transition-colors
        ${isDarkMode 
          ? 'bg-dark-secondary text-dark-accent'
          : 'bg-background text-primary'}
        focus:outline-none focus:ring-2 focus:ring-ring dark:focus:ring-dark-ring focus:ring-offset-2 dark:focus:ring-offset-dark-background
        overflow-hidden shadow-lg hover:shadow-xl ${className}`}
      whileTap={{ scale: 0.92 }}
      initial={{ opacity: 0 }}
      animate={{ 
        opacity: 1,
        boxShadow: isDarkMode 
          ? '0 0 12px 2px rgba(255, 207, 64, 0.3)'
          : '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
      }}
      transition={{ duration: 0.2 }}
      aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      {/* Background glow effect */}
      <div 
        className={`absolute inset-0 rounded-full opacity-20 blur-sm ${
          isDarkMode ? 'bg-dark-accent' : 'bg-primary'
        }`}
      />
      
      {/* Icon animation container */}
      <motion.div
        className="relative h-full w-full flex items-center justify-center"
        initial={false}
        animate={{ 
          rotate: isDarkMode ? 180 : 0,
        }}
        transition={{ 
          type: 'spring', 
          stiffness: 200, 
          damping: 10 
        }}
      >
        {isDarkMode ? (
          <MoonIcon className="h-6 w-6" />
        ) : (
          <SunIcon className="h-6 w-6" />
        )}
      </motion.div>
      
      {/* Glow ring */}
      <motion.div 
        className={`absolute inset-0 rounded-full opacity-0 ${
          isDarkMode ? 'bg-dark-accent' : 'bg-primary'
        }`}
        animate={{ 
          opacity: [0, 0.2, 0],
          scale: [0.8, 1.2, 0.8], 
        }}
        transition={{ 
          duration: 2, 
          repeat: Infinity,
          repeatType: "loop" as const
        }}
      />
    </motion.button>
  );
};

export default ThemeToggle; 